import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ShieldCheck, Dna, HeartPulse, Calendar } from "lucide-react";
import { Layout } from "../components/Layout";
import { WaitlistCapture } from "../components/WaitlistCapture";

const STANDARD = [
  { icon: Dna, title: "DNA Tested", desc: "Every E.O.N Boerboel is DNA verified and microchipped." },
  { icon: HeartPulse, title: "Health Guaranteed", desc: "Genetic health guarantee covering hips, heart and structure." },
  { icon: ShieldCheck, title: "Fully Registered", desc: "Registered with SABBS, NABBR and AKC — papers included." },
  { icon: Calendar, title: "Raised In-Home", desc: "Socialised in-home and released at 8 weeks, fully documented." },
];

export default function Marketplace() {
  return (
    <Layout>
      {/* HERO — clean white */}
      <section className="bg-white border-b border-eon-border" data-testid="puppies-coming-soon">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block text-[11px] font-bold tracking-widest uppercase bg-eon-gold/10 text-eon-gold border border-eon-gold/30 rounded-full px-4 py-1.5">
              Coming Soon
            </span>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase mt-5 leading-[0.95] text-eon-text" data-testid="puppies-heading">
              Puppies Coming Soon
            </h1>
            <p className="mt-6 text-base lg:text-lg text-eon-muted max-w-2xl leading-relaxed">
              E.O.N is preparing the next generation of elite Boerboels. We don't list dogs until they're truly ready —
              no placeholders, no fillers. Join the priority waitlist to be the first to know when litters are announced
              and to get early reservation access.
            </p>
            <div className="mt-8">
              <WaitlistCapture
                subjectType="puppy_waitlist"
                subjectId="eon-puppies"
                placeholder="Enter your email"
                cta="Join the Puppy Waitlist"
              />
            </div>
            <p className="mt-4 text-xs text-eon-muted">Priority access · No spam · Reservations open by application only.</p>
          </motion.div>
        </div>
      </section>

      {/* THE E.O.N STANDARD */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="eon-label mb-3">The E.O.N Standard</div>
          <h2 className="eon-heading text-3xl sm:text-4xl text-eon-text max-w-2xl">
            When our puppies arrive, they arrive done right.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {STANDARD.map((s, i) => (
              <motion.div key={s.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="eon-card p-6" data-testid={`standard-${i}`}>
                <s.icon size={26} className="text-eon-gold" />
                <h3 className="font-display text-lg font-bold text-eon-text mt-4">{s.title}</h3>
                <p className="text-sm text-eon-muted mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED + RESERVATION */}
      <section className="bg-eon-surface/40 border-y border-eon-border">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="eon-label mb-3">Every E.O.N Puppy Comes With</div>
            <ul className="grid sm:grid-cols-2 gap-3 mt-6" data-testid="puppy-package">
              {[
                "NABBA puppy registration", "Health Certificate",
                "Up to date on shots + health record", "Microchipped",
                "DNA tested", "Genetic health guarantee",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 eon-card p-4 text-sm text-eon-text/90">
                  <CheckCircle2 size={18} className="text-eon-gold shrink-0 mt-0.5" /> {item}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-eon-muted">
              Puppies leave at <span className="text-eon-text font-semibold">8 weeks old</span>, fully documented.
            </p>
          </div>
          <div>
            <div className="eon-label mb-3">How Reservations Will Work</div>
            <ol className="mt-6 space-y-4 text-eon-text/80">
              {[
                "Join the waitlist to get litter announcements first",
                "Submit a puppy application when a litter is announced",
                "Our team reviews fit and reaches out to you",
                "Place a deposit to secure your reservation window",
              ].map((s, i) => (
                <li key={s} className="flex gap-4">
                  <span className="font-mono text-eon-gold">0{i + 1}</span><span>{s}</span>
                </li>
              ))}
            </ol>
            <div className="mt-8 grid gap-3">
              <Link to="/breeds/boerboel" className="eon-card p-5 block" data-testid="link-breed-guide">
                <div className="eon-label mb-1">Learn the Breed</div>
                <div className="font-display text-lg font-bold text-eon-text">Boerboel Breed Guide →</div>
              </Link>
              <Link to="/academy" className="eon-card p-5 block" data-testid="link-academy">
                <div className="eon-label mb-1">Owner Education</div>
                <div className="font-display text-lg font-bold text-eon-text">Guardian Ownership Course →</div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
